import { useState } from 'react';
import { useDownload } from '@/hooks/api/useDownloads';

type Platform = 'macos' | 'windows' | 'linux';

interface DownloadButtonProps {
  productId: string;
  platform: Platform;
  version?: string;
  theme: 'dark' | 'light';
  fullWidth?: boolean;
}

export default function DownloadButton({
  productId,
  platform,
  version = 'latest',
  theme,
  fullWidth = false,
}: DownloadButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { mutateAsync: download, isPending } = useDownload();

  const colors = {
    dark: {
      buttonBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      buttonHover: 'linear-gradient(135deg, #5a6fd6 0%, #6a4199 100%)',
      buttonText: '#ffffff',
      errorText: '#ef4444',
      successText: '#10b981',
    },
    light: {
      buttonBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      buttonHover: 'linear-gradient(135deg, #5a6fd6 0%, #6a4199 100%)',
      buttonText: '#ffffff',
      errorText: '#ef4444',
      successText: '#10b981',
    },
  };

  const c = colors[theme];

  const handleDownload = async () => {
    setIsDownloading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await download({
        productId,
        platform,
        version: version === 'latest' ? undefined : version,
      });

      // Initiate download by opening the presigned URL
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(true);

      // Reset success state after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Download failed';
      setError(errorMessage);

      // Reset error state after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsDownloading(false);
    }
  };

  const isLoading = isPending || isDownloading;

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      <button
        onClick={handleDownload}
        disabled={isLoading}
        style={{
          width: fullWidth ? '100%' : 'auto',
          padding: '0.625rem 1.25rem',
          background: isLoading
            ? theme === 'dark'
              ? 'rgba(102, 126, 234, 0.5)'
              : 'rgba(102, 126, 234, 0.7)'
            : c.buttonBg,
          color: c.buttonText,
          border: 'none',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: '600',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s',
          opacity: isLoading ? 0.7 : 1,
        }}
      >
        {isLoading ? (
          <>
            <LoadingSpinner />
            <span>Downloading...</span>
          </>
        ) : success ? (
          <>
            <span>✓</span>
            <span>Downloaded!</span>
          </>
        ) : (
          <>
            <DownloadIcon />
            <span>Download</span>
          </>
        )}
      </button>

      {error && (
        <p
          style={{
            color: c.errorText,
            fontSize: '0.75rem',
            marginTop: '0.5rem',
            textAlign: 'center',
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{
        animation: 'spin 1s linear infinite',
      }}
    >
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" strokeOpacity="1" />
    </svg>
  );
}
